import api from '../config/api';

const AUTH_TOKEN_KEY = 'admin_token';
const AUTH_USER_KEY = 'admin_user';

// Get stored token
export const getToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

// Get stored user
export const getUser = () => {
  const userStr = localStorage.getItem(AUTH_USER_KEY);
  try {
    return userStr ? JSON.parse(userStr) : null;
  } catch (_) {
    return null;
  }
};

// Store auth data
export const setAuth = (token, user) => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  // Set token in axios defaults
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

// Clear auth data
export const clearAuth = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  delete api.defaults.headers.common['Authorization'];
};

// Initialize axios with token if available
const token = getToken();
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Minutes until a 429 (rate limited) response allows a retry. Reads the standard
// Retry-After / RateLimit-Reset headers when the browser can see them, otherwise
// falls back to the limiter window (15 minutes).
export const getRetryAfterMinutes = (error) => {
  const headers = error?.response?.headers || {};
  const raw = headers['retry-after'] || headers['ratelimit-reset'] || headers['x-ratelimit-reset'];
  const seconds = parseInt(raw, 10);
  if (!Number.isNaN(seconds) && seconds > 0) return Math.max(1, Math.ceil(seconds / 60));
  return 15;
};

export const isRateLimitError = (error) => error?.response?.status === 429;

export const rateLimitMessage = (error) => {
  const minutes = getRetryAfterMinutes(error);
  return `Too many attempts. Please try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};

// Error thrown when a session could not be verified for a temporary reason
// (rate limited or the server was unreachable). Callers must keep the session.
export class TransientAuthError extends Error {
  constructor(message, error) {
    super(message);
    this.name = 'TransientAuthError';
    this.transient = true;
    this.status = error?.response?.status;
    this.retryAfterMinutes = isRateLimitError(error) ? getRetryAfterMinutes(error) : 1;
  }
}

// Login
export const login = async (username, password) => {
  try {
    const response = await api.post('/auth/login', { username, password });
    const { token, user } = response.data;
    setAuth(token, user);
    return { token, user };
  } catch (error) {
    // Better error handling
    if (error.response) {
      if (isRateLimitError(error)) {
        throw Object.assign(new Error(rateLimitMessage(error)), { status: 429 });
      }
      // Server responded with error status
      const data = error.response.data;
      const message = (data && typeof data === 'object' && (data.error || data.message)) || 'Login failed';
      throw Object.assign(new Error(message), { status: error.response.status });
    } else if (error.request) {
      // Request made but no response (network error)
      throw new Error('Cannot connect to server. Please make sure the backend server is running.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Login failed');
    }
  }
};

// Verify token. A 429 or a network failure is NOT an invalid session: the stored
// session is kept and a TransientAuthError is thrown so the caller can retry later.
export const verifyToken = async () => {
  try {
    const response = await api.get('/auth/verify');
    return response.data.user;
  } catch (error) {
    if (isRateLimitError(error) || !error.response) {
      throw new TransientAuthError(
        isRateLimitError(error) ? rateLimitMessage(error) : 'Server unreachable, session will be re-checked shortly.',
        error
      );
    }
    clearAuth();
    const data = error.response?.data;
    throw new Error((data && typeof data === 'object' && data.error) || 'Token verification failed');
  }
};

// Logout
export const logout = () => {
  clearAuth();
};
